import * as XLSX from 'xlsx'
import type { Job } from '@/types/jobs'
import { getJobStatusDisplay, type JobStatus } from '@/types/jobs'
import type { Addresses as Address, Customers as Customer } from '@/types/pbSchema'
import type { Tag } from '@/types/tags'
import { formatAddress } from './address'

export interface JobsExportRow {
    job: Job
    customer?: Customer
    address?: Address
    tags: Tag[]
}

const HEADERS = [
    'Identifier',
    'Name',
    'Status',
    'Customer',
    'Address',
    'Tags',
    'Notes',
    'Started',
    'Completed',
    'Created',
]

function formatDate(value: string | undefined | null): string {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString()
}

function buildRow(row: JobsExportRow): (string | number)[] {
    const { job, customer, address, tags } = row
    const tagNames = tags
        .map(t => t.name)
        .filter(Boolean)
        .join(', ')
    const addressText = formatAddress(address)

    return [
        job.identifier,
        job.name ?? '',
        getJobStatusDisplay(job.status as JobStatus),
        customer?.name ?? '',
        addressText,
        tagNames,
        job.notes ?? '',
        formatDate(job.started),
        formatDate(job.completed),
        formatDate(job.created),
    ]
}

export function generateJobsXLSX(rows: JobsExportRow[]): Uint8Array {
    const sheetRows = [HEADERS, ...rows.map(buildRow)]
    const sheet = XLSX.utils.aoa_to_sheet(sheetRows)

    for (let col = 0; col < HEADERS.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col })
        const cell = sheet[cellRef]
        if (cell) {
            cell.s = { font: { bold: true } }
        }
    }

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, sheet, 'Jobs')

    return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' }) as Uint8Array
}
